// Copyright 2022, Imprivata, Inc.  All rights reserved.
import {
 Button, TypeIcon } from '@imprivata-cloud/components';
import { Alert, Flex, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetGrantStatusQuery,
  useGetPatientQuery,
  useGetPhotoQuery,
  useVerifyPatientMutation,
} from '../../api/hooks';
import { usePostGrantMutation } from '../../api/hooks/postGrantMutation';
import CameraCard from '../../components/camera/CameraCard';
import DemographicsCard from '../../components/demographics/DemographicsCard';
import LoadingSpinner from '../../components/loading/Loading';
import { useFaceCapture } from './hooks/useFaceCapture';
import './VerifyPage.less';
import { ErrorComponent } from '../../components/error/ErrorComponent';
import Photo from '../../components/photo/Photo';
import { navigateWithContext } from '@utils/NavigationHelper';
import { useTranslation } from 'react-i18next';
import { tracingService, generateBaseAttributes } from '../../tracing';

const { Title, Text } = Typography;
const maxFailedAttempts = 2;
const redirectDelayMs = 2000;
const VerifyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Ref to store retry timeout ID for cleanup
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to track the last processed API error to prevent duplicate retries
  const lastProcessedErrorRef = useRef<string | null>(null);
  // Ref to track if we've already submitted the current capture to prevent duplicate API calls
  const lastSubmittedPhotoRef = useRef<string | null>(null);
  // Ref to store the verify patient mutation function to avoid circular dependencies
  const verifyPatientRef = useRef<((photoBase64: string) => void) | null>(null);
  // Ref to store the retry function to avoid stale closures
  const retryRef = useRef<(() => Promise<void>) | null>(null);
  // Parse query parameters
  const { enableDebugLogging, tenantId } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const debugEnabled = params.get('debug') === 'true';
    const tenant = params.get('Impr-Tenant-Id');
    if (debugEnabled) {
      console.log('[VerifyPage] Debug logging enabled via query parameter');
    }
    return { enableDebugLogging: debugEnabled, tenantId: tenant };
  }, []);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [disableWorkflow, setDisableWorkflow] = useState<boolean>(false);
  const [verificationSuccess, setVerificationSuccess] = useState<boolean>(false);
  // Track failed attempts in a ref to avoid stale closures
  // Note: This ref is manually synchronized in handleVerifySuccess to avoid race conditions
  const failedAttemptsRef = useRef<number>(0);
  // Grant status and patient queries
  const {
    grantStatus,
    isLoading: isGrantStatusLoading,
    enable: enableGrantStatusQuery,
  } = useGetGrantStatusQuery(false);
  const {
    patient,
    isLoading: isPatientLoading,
    enable: enablePatientQuery,
  } = useGetPatientQuery(false);
  const { photo } = useGetPhotoQuery(patient?.photoUrl);
  // Memoize the capture complete callback to prevent infinite loops
  const handleCaptureComplete = useCallback(
    (photoBase64: string) => {
      // Prevent duplicate submissions of the same photo
      if (lastSubmittedPhotoRef.current === photoBase64) {
        if (enableDebugLogging) {
          console.log('[VerifyPage] Skipping duplicate photo submission');
        }
        return;
      }
      if (enableDebugLogging) {
        console.log('[VerifyPage] Submitting photo for verification');
      }
      // Track this photo as submitted
      lastSubmittedPhotoRef.current = photoBase64;
      // Clear error tracking when starting a new verification attempt
      lastProcessedErrorRef.current = null;
      
      // Add event to span
      tracingService.addEvent('photo_captured', {
        photoSize: photoBase64.length,
      });

      // When capture is complete, call the verification API
      if (verifyPatientRef.current) {
        verifyPatientRef.current(photoBase64);
      }
    },
    [enableDebugLogging],
  );

  // Handle capture button click
  const handleCaptureClick = async () => {
    // Start tracing span for face capture
    tracingService.startSpan('face_verification_capture', {
      ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
      page: 'verify',
      action: 'capture_button_click',
    });

    try {
      await handleCapture();
    } catch (error) {
      // Mark error in span if capture fails
      tracingService.endSpan(undefined, error as Error);
      throw error;
    }
  };

  // Update useEffect for verification success to end the span
  useEffect(() => {
    if (verificationSuccess) {
      // End the span successfully
      tracingService.endSpan({
        ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
        verification_status: 'success',
        patient_id: verificationSuccess,
      });
    }
  }, [verificationSuccess, tenantId]);

  // Use the face capture hook to manage camera state
  const {
    videoRef,
    canvasRef,
    captureState,
    isInitializing,
    cameraError,
    handleCapture,
    retry,
    onVideoElementReady,
    onVideoPlay,
  } = useFaceCapture({
    enableParavisionSdk: true,
    enableDebugLogging,
    onCaptureComplete: handleCaptureComplete,
  });
  // Store retry function in ref to avoid stale closures
  retryRef.current = retry;
  // Handle navigation to account page after max failed attempts
  const navigateToAccount = useCallback(() => {
    navigateWithContext(navigate, '/account', {
      state: {
        fromVerifyFailure: true,
      },
    });
  }, [navigate]);
  // Post grant to trigger redirect back to MyChart when closing
  const { postGrant } = usePostGrantMutation({});
  const handleReturn = useCallback(() => {
    postGrant();
  }, [postGrant]);
  // Handle retry logic after a failed verification attempt
  const scheduleRetry = useCallback(() => {
    // Clear submitted photo tracking to allow retry
    lastSubmittedPhotoRef.current = null;
    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    // Retry video after a short delay - this allows the camera to render first
    retryTimeoutRef.current = setTimeout(async () => {
      retryTimeoutRef.current = null;
      if (retryRef.current) {
        await retryRef.current();
      }
    }, 1000);
  }, []);
  // Handle verification failure that should redirect to account page
  const handleMaxAttemptsReached = useCallback(() => {
    // Use updated i18n key for max attempts reached message
    setErrorMessage(t('verifyPage.face-verification-failed-redirecting'));
    setDisableWorkflow(true);
    setTimeout(() => {
      navigateToAccount();
    }, redirectDelayMs);
  }, [navigateToAccount]);
  // Handle verification failure that should retry
  const handleRetryableFailure = useCallback(
    (message: string) => {
      setErrorMessage(message);
      scheduleRetry();
    },
    [scheduleRetry],
  );
  // Memoize the verification success callback to prevent mutation recreation
  const handleVerifySuccess = useCallback(
    async (patientId: string) => {
      if (patientId && patientId !== '') {
        setVerificationSuccess(true);
        failedAttemptsRef.current = 0;
        setErrorMessage(undefined);
        // Clear submitted photo tracking on success
        lastSubmittedPhotoRef.current = null;
        // Enable grant status query to check pendingHandoff
        enableGrantStatusQuery();
      } else {
        console.error('Verification failed');
        failedAttemptsRef.current++;
        if (failedAttemptsRef.current >= maxFailedAttempts) {
          handleMaxAttemptsReached();
        } else {
          handleRetryableFailure(t('verifyPage.face-verification-failed-try-again'));
        }
      }
    },
    [enableGrantStatusQuery, handleMaxAttemptsReached, handleRetryableFailure],
  );
  const verifyPatientMutation = useVerifyPatientMutation({
    onSuccess: handleVerifySuccess,
  });
  // Store the verify function in a ref to avoid circular dependencies
  verifyPatientRef.current = verifyPatientMutation.verifyPatient;

  // Update useEffect for verification errors to end the span
  useEffect(() => {
    if (verifyPatientMutation.apiError) {
      // End span with error
      tracingService.endSpan(
        {
          ...generateBaseAttributes(tracingService.getWorkflowId(), tenantId),
          verification_status: 'error',
          error_code: verifyPatientMutation.apiError.error.code,
          error_message: verifyPatientMutation.apiError.error.message,
        },
        new Error(verifyPatientMutation.apiError.error.message)
      );
    }
  }, [verifyPatientMutation.apiError, tenantId]);

  // Handle API errors directly (removed multi-instance forwarding logic)
  useEffect(() => {
    if (verifyPatientMutation.apiError) {
      const errorKey = `${verifyPatientMutation.apiError.error.code}-${verifyPatientMutation.apiError.error.message}`;
      if (lastProcessedErrorRef.current === errorKey) {
        return;
      }
      lastProcessedErrorRef.current = errorKey;
      const errorCode = verifyPatientMutation.apiError.error.code;
      if (errorCode === 'rate-limit-exceeded') {
        setErrorMessage(t('verifyPage.too-many-attempts-try-again-later'));
        setDisableWorkflow(true);
        return;
      }
      if (errorCode === 'session-id-not-found') {
        setErrorMessage(t('verifyPage.unexpected-error-try-again-later'));
        setDisableWorkflow(true);
        return;
      }
      failedAttemptsRef.current++;
      if (failedAttemptsRef.current >= maxFailedAttempts) {
        handleMaxAttemptsReached();
      } else {
        const ERROR_MESSAGE_MAP: Record<string, string> = {
          'Something went wrong': 'verifyPage.errors.generic'
        };
        const rawMessage = verifyPatientMutation.apiError.error.message;
        const message = t(ERROR_MESSAGE_MAP[rawMessage as string] || 'verifyPage.verification-failed');
        handleRetryableFailure(message);
      }
    } else {
      lastProcessedErrorRef.current = null;
    }
  }, [verifyPatientMutation.apiError, handleMaxAttemptsReached, handleRetryableFailure]);
  // Enable patient query when grant status is loaded and not pending handoff
  useEffect(() => {
    if (
      verificationSuccess &&
      grantStatus &&
      !isGrantStatusLoading &&
      !grantStatus.pendingHandoff
    ) {
      enablePatientQuery();
    }
  }, [verificationSuccess, grantStatus, isGrantStatusLoading, enablePatientQuery]);
  // Sync camera error to page error message
  useEffect(() => {
    if (cameraError) {
      setErrorMessage(cameraError);
    }
  }, [cameraError]);
  // Cleanup: clear any pending retry timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);
  // Show verification in progress
  // Only show loading when the mutation is pending AND we haven't failed yet (no error message)
  const isVerifying =
    (verifyPatientMutation.isPending && !errorMessage) ||
    (verificationSuccess && isGrantStatusLoading) ||
    (verificationSuccess && isPatientLoading);
  if (isVerifying) {
    console.log('isVerifying', isVerifying);
    console.log('verifyPatientMutation.isPending', verifyPatientMutation.isPending);
    console.log('errorMessage', errorMessage);
    console.log('verificationSuccess', verificationSuccess);
    console.log('isGrantStatusLoading', isGrantStatusLoading);
    console.log('isPatientLoading', isPatientLoading);
    return (
      <Flex vertical align="center">
        <Title level={2} data-testid="verify-title">
          {t('verifyPage.verifying-your-identity')}
        </Title>
        <LoadingSpinner />
      </Flex>
    );
  }
  // Show success state
  if (verificationSuccess) {
    // Show handoff message if pendingHandoff is true
    if (grantStatus?.pendingHandoff) {
      return (
        <Flex
          vertical
          align="center"
          justify="center"
          className="verify-page"
          style={{ marginTop: '1rem', padding: '0.5rem' }}
        >
          <TypeIcon type="info" style={{ width: 100, height: 100 }} />
          <Title level={2} className="text-center" data-testid="verify-title">
            {t('verifyPage.identity-verification-completed', { defaultValue: 'Identity verification completed.' })}
          </Title>
          <Text className="text-center" data-testid="verify-instructions">
            {t('verifyPage.you-can-close-this-window-and-return-to-your-original-device', { defaultValue: 'You can close this window and return to your original device.' })}
          </Text>
        </Flex>
      );
    }
    // Show patient demographics and continue button if not pending handoff
    if (patient) {
      return (
        <Flex
          gap={30}
          vertical
          align="center"
          className="verify-page w-100"
          style={{ marginTop: '1rem', padding: '0.5rem' }}
        >
          <Photo image={photo ?? undefined} />
          <DemographicsCard
            givenName={patient.givenName}
            surname={patient.surname}
            dateOfBirth={patient.dateOfBirth}
            mrn={patient.mrn}
            isLoading={isPatientLoading}
          />
          <Alert
            message={t('verifyPage.successful-verification')}
            type="success"
            className="w-100 impr-alert impr-alert-success"
            icon={<TypeIcon type="success" />}
            showIcon={true}
            data-testid="verify-success-alert"
          />
          <Button
            type="primary"
            label={t('verifyPage.continue')}
            onClick={() => navigateWithContext(navigate, '/consent')}
            disabled={!grantStatus?.pendingAuthorization}
            className="w-100"
            autoFocus
            data-testid="verify-continue-btn"
          />
        </Flex>
      );
    }
  }
  return (
    <Flex vertical align="center" className="verify-page">
      {captureState.feedback.cameraError ? (
        <>
          <ErrorComponent cause="camera-access-required" />
          <Button
            type="primary"
            label={t('verifyPage.try-again')}
            onClick={retry}
            className="capture-button mt-1"
            autoFocus={!(disableWorkflow || isInitializing)}
            data-testid="retry-button"
          />
          <Button
            type="default"
            label={t('verifyPage.return-to-mychart')}
            onClick={handleReturn}
            className="capture-button ml-2 mt-1"
          />
        </>
      ) : (
        <>
          <Title level={2} className="verify-title" data-testid="verify-title">
            {t('verifyPage.verify-your-identity')}
          </Title>
          <Text className="verify-instructions" data-testid="verify-instructions">
            {t('verifyPage.take-a-photo-of-your-face-to-verify-your-identity')}
          </Text>
          {errorMessage && (
            <Flex
              align="center"
              gap={12}
              className="warning-banner bg-gray"
              data-testid="warning-banner"
            >
              <TypeIcon type="warning" className="warning-icon" data-testid="warning-icon" />
              <Typography.Text className="warning-text" data-testid="warning-text">
                {errorMessage}
              </Typography.Text>
            </Flex>
          )}
          <div className="verification-content">
            <CameraCard
              capturing={captureState.capturing}
              captured={captureState.captured}
              video={videoRef}
              canvas={canvasRef}
              cameraError={captureState.feedback.cameraError}
              isInitializing={isInitializing}
              onVideoPlay={onVideoPlay}
              onVideoElementReady={onVideoElementReady}
            />
          </div>
          {/* Capture button: always render (even for secondary instances) so tests can find it */}
          <Button
            type="primary"
            onClick={handleCaptureClick}
            disabled={disableWorkflow || isInitializing}
            className="capture-button mt-1"
            autoFocus={!(disableWorkflow || isInitializing)}
            data-testid="capture-button"
            label={t('verifyPage.capture')}
          />
        </>
      )}
    </Flex>
  );
};
export default VerifyPage;